import { bpsText, enumText } from './presentation';
import { EnvironmentBanner } from './environment-banner';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from 'react';

export function trapDialogFocus(event: KeyboardEvent<HTMLDialogElement>) {
  if (event.key !== 'Tab') return;
  const targets = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], summary, [tabindex="0"]',
    ),
  ).filter((n) => n.getClientRects().length > 0);
  const first = targets[0];
  if (!first) {
    event.preventDefault();
    event.currentTarget.focus();
    return;
  }
  event.preventDefault();
  const current = targets.indexOf(document.activeElement as HTMLElement);
  const next =
    current < 0
      ? event.shiftKey
        ? targets.length - 1
        : 0
      : (current + (event.shiftKey ? -1 : 1) + targets.length) % targets.length;
  targets[next]?.focus();
}

export const formatValue = (v: unknown): string =>
  typeof v === 'number'
    ? v.toLocaleString('tr-TR')
    : typeof v === 'boolean'
      ? v
        ? 'Açık'
        : 'Kapalı'
      : v == null
        ? '—'
        : typeof v === 'object'
          ? JSON.stringify(v)
          : String(v);
export type Change = { label: string; before: unknown; after: unknown; unit?: 'bps' };
type Confirmation = {
  title: string;
  description: string;
  changes: Change[];
  danger?: boolean;
  typedValue?: string;
};
const ConfirmationContext = createContext<(p: Confirmation) => Promise<boolean>>(async () => false);
export const useConfirmation = () => useContext(ConfirmationContext);

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState<Confirmation | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const lastAction = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const remember = (event: PointerEvent) => {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>('button, input[type="submit"]')
          : null;
      if (target && !target.closest('.confirm-dialog')) lastAction.current = target;
    };
    document.addEventListener('pointerdown', remember, true);
    return () => document.removeEventListener('pointerdown', remember, true);
  }, []);
  const resolve = useRef<((v: boolean) => void) | null>(null);
  const finish = (value: boolean) => {
    dialog.current?.close();
    resolve.current?.(value);
    resolve.current = null;
    setPending(null);
    requestAnimationFrame(() => {
      if (trigger.current?.isConnected) trigger.current.focus();
    });
  };
  useEffect(() => {
    if (pending) dialog.current?.showModal();
  }, [pending]);
  useEffect(() => () => resolve.current?.(false), []);
  return (
    <ConfirmationContext.Provider
      value={(request) => {
        if (resolve.current) return Promise.resolve(false);
        trigger.current = lastAction.current?.isConnected
          ? lastAction.current
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        return new Promise<boolean>((r) => {
          resolve.current = r;
          setTyped('');
          setPending(request);
        });
      }}
    >
      {children}
      <dialog
        className="confirm-dialog"
        onKeyDown={trapDialogFocus}
        ref={dialog}
        aria-labelledby="confirm-title"
        aria-describedby="confirm-description"
        onCancel={(e) => {
          e.preventDefault();
          finish(false);
        }}
      >
        {pending && (
          <>
            <EnvironmentBanner />
            <p className="eyebrow">
              İşlem önizlemesi ·{' '}
              {
                pending.changes.filter((c) => JSON.stringify(c.before) !== JSON.stringify(c.after))
                  .length
              }{' '}
              alan değişiyor
            </p>
            <h2 id="confirm-title">{pending.title}</h2>
            <p id="confirm-description">{pending.description}</p>
            <div className="change-list">
              {pending.changes
                .filter((c) => JSON.stringify(c.before) !== JSON.stringify(c.after))
                .map((c, i) => (
                  <div className="change" key={i}>
                    <strong>{c.label}</strong>
                    <span>
                      <span className="change-before">
                        <small>Sunucudaki değer</small>
                        {c.unit === 'bps' && typeof c.before === 'number'
                          ? bpsText(c.before)
                          : ['active', 'banned', 'frozen', 'BONUS', 'PURCHASED', 'GIFTED'].includes(
                                String(c.before),
                              )
                            ? enumText(String(c.before))
                            : formatValue(c.before)}
                      </span>
                      <span aria-label="yerine"> → </span>
                      <b className="change-after">
                        <small>Senin taslağın</small>
                        {c.unit === 'bps' && typeof c.after === 'number'
                          ? bpsText(c.after)
                          : ['active', 'banned', 'frozen', 'BONUS', 'PURCHASED', 'GIFTED'].includes(
                                String(c.after),
                              )
                            ? enumText(String(c.after))
                            : formatValue(c.after)}
                      </b>
                    </span>
                  </div>
                ))}
            </div>
            {pending.typedValue && (
              <label className="typed-confirm">
                Yüksek tutarlı çip basımı · onay için{' '}
                {Number(pending.typedValue).toLocaleString('tr-TR')} tutarını yaz
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label="Tutarı tekrar yaz"
                  value={typed}
                  onPaste={(e) => e.preventDefault()}
                  onChange={(e) => setTyped(e.target.value)}
                />
                <small>Tutarı elle yazmadan işlem uygulanmaz.</small>
              </label>
            )}
            <div className="dialog-actions">
              <button autoFocus onClick={() => finish(false)}>
                Vazgeç
              </button>
              <button
                className={pending.danger ? 'danger' : 'primary'}
                disabled={
                  !!pending.typedValue && typed.replace(/[.\s]/g, '') !== pending.typedValue
                }
                onClick={() => finish(true)}
              >
                Onayla ve uygula
              </button>
            </div>
          </>
        )}
      </dialog>
    </ConfirmationContext.Provider>
  );
}

export function PageState({
  loading,
  error,
  empty,
  onRetry,
}: {
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  onRetry?: () => void;
}) {
  if (error)
    return (
      <div role="alert" className="state-box error">
        <strong>
          {error.includes('FORBIDDEN') ? 'Bu işlem için yetkin yok.' : 'Veri alınamadı.'}
        </strong>
        <p>{error}</p>
        {onRetry && <button onClick={onRetry}>Yeniden dene</button>}
      </div>
    );
  if (loading)
    return (
      <div role="status" className="state-box muted">
        Veriler yükleniyor…
      </div>
    );
  if (empty)
    return (
      <div role="status" className="state-box muted">
        Bu görünümde henüz kayıt yok.
      </div>
    );
  return null;
}

/** Türkçe binlik ayırıcılı tam sayı; boş/geçersiz giriş sessizce sıfıra çevrilmez. */
export function NumberField({
  value,
  onChange,
  label,
  unit,
  disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
  unit?: string;
  disabled?: boolean;
}) {
  const [text, setText] = useState(formatValue(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(formatValue(value));
  }, [value]);
  return (
    <label>
      {label}
      <span className="number-field">
        <input
          aria-label={label}
          onFocus={() => {
            focused.current = true;
          }}
          inputMode="numeric"
          required
          disabled={disabled}
          value={text}
          onChange={(e) => {
            const s = e.target.value;
            setText(s);
            const clean = s.replace(/[.\s]/g, '');
            const valid = /^-?\d+$/.test(clean) && Number.isSafeInteger(Number(clean));
            e.target.setCustomValidity(valid ? '' : 'Geçerli bir tam sayı gir.');
            if (valid) onChange(Number(clean));
          }}
          onBlur={(e) => {
            focused.current = false;
            if (e.target.validity.valid) setText(formatValue(value));
          }}
        />
        {unit && <span>{unit}</span>}
      </span>
    </label>
  );
}

export function DialogPanel({
  children,
  label,
  onClose,
  drawer = false,
}: {
  children: ReactNode;
  label: string;
  onClose: () => void;
  drawer?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    const trigger = document.activeElement as HTMLElement | null;
    d?.showModal();
    return () => {
      d?.close();
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={`dialog-panel${drawer ? ' record-drawer' : ''}`}
      onKeyDown={trapDialogFocus}
      aria-label={label}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {children}
    </dialog>
  );
}
